import type { StageItem, StageItemKind } from './stage-items'

/**
 * "Sync with Everyone" as a mode rather than a one-off pull.
 *
 * The one-off version moved every viewer once and kept nothing: the moderator
 * who pressed it had no state to show, and any viewer could leave the pick a
 * second later. While a sync is on here, the moderator who turned it on (the
 * owner) drives the stage for the whole room: each tab they switch to is
 * pushed, viewers may glance elsewhere but the next push brings them back, and
 * nobody can opt out short of the owner or the host turning it off.
 *
 * Each client holds its own copy, built from the messages the owner sends.
 * There is no server-side record: the room channel only relays them, the same
 * way the one-off sync always travelled. That is also why the copy has to be
 * dropped whenever the owner can no longer be the one sending (they left,
 * refreshed, or the stage no longer has anything to choose between).
 *
 * Pure functions over an immutable value; each returns its input unchanged
 * when nothing happened, like `stage-selection`.
 */

export type StageSync = {
  /** The moderator who turned the sync on, and the only one who pushes. */
  readonly ownerPeerId: string
  readonly ownerName: string
  /** The last item the owner pushed, as the owner identified it. */
  readonly targetSyncId: string | undefined
  readonly targetKind: StageItemKind | undefined
} | null

export type SyncPush = {
  by: string
  ownerName: string
  syncId: string | undefined
  kind: StageItemKind | undefined
}

export type RoomRoleName = 'host' | 'cohost' | 'attendee'

/** The local moderator turns it on, starting from what they are looking at. */
export function startSync(ownerPeerId: string, ownerName: string, current: StageItem | null): StageSync {
  return {
    ownerPeerId,
    ownerName,
    targetSyncId: current?.syncId,
    targetKind: current?.kind,
  }
}

/**
 * The owner moved on (or a sync was announced to us). Last one wins: if a
 * second moderator manages to turn it on in the same moment, everyone ends up
 * agreeing on whoever was heard last rather than on two owners.
 */
export function applyPush(prev: StageSync, push: SyncPush): StageSync {
  if (
    prev &&
    prev.ownerPeerId === push.by &&
    prev.ownerName === push.ownerName &&
    prev.targetSyncId === push.syncId &&
    prev.targetKind === push.kind
  ) {
    return prev
  }
  return {
    ownerPeerId: push.by,
    ownerName: push.ownerName,
    targetSyncId: push.syncId,
    targetKind: push.kind,
  }
}

/** The owner's own copy follows what the owner is looking at. */
export function retarget(prev: StageSync, current: StageItem | null): StageSync {
  if (!prev || !current) return prev
  if (prev.targetSyncId === current.syncId && prev.targetKind === current.kind) return prev
  return { ...prev, targetSyncId: current.syncId, targetKind: current.kind }
}

/**
 * Someone asked to turn it off. Only the owner or the host may, so an off
 * from anyone else (a stale client, another co-host) is ignored.
 */
export function applyOff(prev: StageSync, by: string | undefined, byRole: RoomRoleName): StageSync {
  if (!prev || !by) return prev
  if (by === prev.ownerPeerId || byRole === 'host') return null
  return prev
}

/** The owner's media session ended: nobody is left to push. */
export function ownerLeft(prev: StageSync, peerId: string | undefined): StageSync {
  return prev && peerId && peerId === prev.ownerPeerId ? null : prev
}

type PresenceMetaLike = { phx_ref_prev?: unknown }
type PresenceDiffLike = {
  joins?: Record<string, { metas?: PresenceMetaLike[] }>
  leaves?: Record<string, { metas?: PresenceMetaLike[] }>
}

/**
 * Presence keys that arrived as a new connection, not as a state change.
 *
 * Presence reports a camera, mic or hand-raise change as a leave of the old
 * meta plus a join of the new one, and that join carries `phx_ref_prev`. Only
 * a join without it is somebody actually (re)connecting.
 */
export function freshJoins(diff: PresenceDiffLike): string[] {
  const out: string[] = []
  for (const [key, entry] of Object.entries(diff.joins ?? {})) {
    const metas = entry?.metas ?? []
    if (metas.some((m) => m?.phx_ref_prev === undefined)) out.push(key)
  }
  return out
}

/**
 * The owner's connection went away or came back new.
 *
 * A refresh does not produce `media:peer_left` for 30 seconds (the server
 * waits that long for a dropped connection to return), so presence is the
 * earlier signal. Gone without a matching join means the tab closed or
 * reloaded; a fresh join means a reloaded tab, which starts with no sync of its
 * own and so cannot keep pushing. Either way the sync ends.
 */
export function applyPresence(prev: StageSync, diff: PresenceDiffLike): StageSync {
  if (!prev) return prev
  const owner = prev.ownerPeerId
  const joined = !!diff.joins?.[owner]
  const left = !!diff.leaves?.[owner]
  if (left && !joined) return null
  if (freshJoins(diff).includes(owner)) return null
  return prev
}

/**
 * The stage went from a choice to no choice. With fewer than two items the
 * switcher is not shown at all, so a sync left on would be invisible and
 * impossible to turn off. Only the transition counts: a client that joined
 * late and is still loading its items has not "dropped" below two.
 */
export function applyItemCount(prev: StageSync, before: number, after: number): StageSync {
  if (!prev) return prev
  return before >= 2 && after < 2 ? null : prev
}

export function isSyncOwner(sync: StageSync, localPeerId: string | null | undefined): boolean {
  return !!sync && !!localPeerId && sync.ownerPeerId === localPeerId
}

/**
 * Who may press the sync control. Off: any moderator, to turn it on. On: the
 * owner, to turn it off. The host turns someone else's sync off from the
 * "Sync on by" chip instead, see `hostCanTurnOff`.
 */
export function canToggleSync(sync: StageSync, localPeerId: string | null | undefined, isModerator: boolean): boolean {
  if (!sync) return isModerator
  return isSyncOwner(sync, localPeerId)
}

export function hostCanTurnOff(sync: StageSync, localPeerId: string | null | undefined, myRole: RoomRoleName): boolean {
  return !!sync && !isSyncOwner(sync, localPeerId) && myRole === 'host'
}

/** Being driven by someone else's sync: no opting out. */
export function isLockedFollower(sync: StageSync, localPeerId: string | null | undefined): boolean {
  return !!sync && !isSyncOwner(sync, localPeerId)
}

/**
 * Find the owner's pick among this client's items. Exact identity first; the
 * kind fallback covers a sender that cannot name one item of a kind (mobile
 * tracks one content view per kind, so its screen is just `screen`).
 */
export function resolveTarget(
  items: readonly StageItem[],
  syncId: string | undefined,
  kind: StageItemKind | undefined,
): StageItem | undefined {
  return (
    (syncId ? items.find((i) => i.syncId === syncId) : undefined) ??
    (kind ? items.find((i) => i.kind === kind) : undefined)
  )
}
